// src/components/simulation/SimulatedOS.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Laptop, Mail, Globe, FileText, Folder, Shield, Bell, 
  Clock, Calendar, Settings, Search, User, X, Maximize2, Minimize2, BookOpen 
} from 'lucide-react';

const SimulatedOS = ({ 
  osType = 'windows', 
  onApplicationLaunch, 
  initialActiveApp = null,
  notifications = [],
  children // Accept notifications from parent
}) => {
  const [activeApp, setActiveApp] = useState(initialActiveApp);
  const [time, setTime] = useState(new Date());
  const [localNotifications, setLocalNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const navigate = useNavigate();
  
  // Update clock
  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  // Merge incoming notifications with local ones
  useEffect(() => {
    if (notifications.length > 0) {
      setLocalNotifications(prev => [
        ...prev,
        ...notifications.map(n => ({
          id: n.id || `notification-${Date.now()}-${Math.random()}`,
          timestamp: n.timestamp || new Date(),
          arenaId: n.arenaId || 'os_security', // Add arenaId with fallback
          ...n
        }))
      ]);
    }
  }, [notifications]);
  
  // Handle incoming notifications (could be from parent component or events)
  useEffect(() => {
    // Example notification that could be triggered by scenario events
    const demoNotification = setTimeout(() => {
      addNotification({
        id: 'notification-1',
        title: 'Security Alert',
        message: 'Unusual login attempt detected',
        type: 'warning',
        arenaId: 'password_security',
        timestamp: new Date()
      });
    }, 10000);
    
    return () => clearTimeout(demoNotification);
  }, []);
  
  const addNotification = (notification) => {
    setLocalNotifications(prev => [...prev, {
      id: notification.id || `notification-${Date.now()}`,
      timestamp: notification.timestamp || new Date(),
      arenaId: notification.arenaId || 'os_security',
      ...notification
    }]);
  };
  
  const dismissNotification = (notificationId) => {
    setLocalNotifications(prev => prev.filter(n => n.id !== notificationId));
  };
  
  const launchApp = (appId) => {
    setActiveApp(appId);
    setStartMenuOpen(false);
    if (onApplicationLaunch) {
      onApplicationLaunch(appId);
    }
  };
  
  // Handle notification icon click
  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
  };
  
  const handleLearnMore = (arenaId) => {
    const notification = localNotifications.find(n => n.arenaId === arenaId);
    if (notification?.learnMoreLink) {
      window.open(notification.learnMoreLink, '_blank', 'noopener,noreferrer');
    } else if (arenaId) {
      // Fallback to original behavior
      navigate(`/learning/module/${arenaId}?topic=${arenaId}`);
    }
  };

  // Render the notification panel
  const renderNotificationPanel = () => {
    if (!showNotifications || localNotifications.length === 0) return null;
    
    return (
      <div className="absolute top-12 right-0 w-[92vw] max-w-80 bg-gray-900 border border-gray-700 rounded-lg shadow-xl overflow-hidden z-50">
        <div className="p-3 border-b border-gray-800 flex justify-between items-center">
          <h3 className="font-bold">Notifications</h3>
          <button 
            className="text-gray-400 hover:text-white"
            onClick={() => setLocalNotifications([])}
          >
            Clear All
          </button>
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {localNotifications.map(notification => (
            <div 
              key={notification.id} 
              className={`p-3 border-b border-gray-800 ${
                notification.type === 'warning' ? 'bg-yellow-900/20' : 
                notification.type === 'error' ? 'bg-red-900/20' : 
                notification.type === 'success' ? 'bg-green-900/20' :
                notification.type === 'info' ? 'bg-blue-900/20' : 'bg-gray-800'
              }`}
            >
              <div className="flex justify-between">
                <div className="font-medium">{notification.title}</div>
                <button 
                  className="text-gray-500 hover:text-white"
                  onClick={() => dismissNotification(notification.id)}
                >
                  <X size={14} />
                </button>
              </div>
              <p className="text-sm text-gray-300 mt-1">{notification.message}</p>
              <div className="text-xs text-gray-500 mt-1">
                {new Date(notification.timestamp).toLocaleTimeString()}
              </div>
              {/* Learn More Button */}
              <div className="mt-2">
                <button
                  onClick={() => handleLearnMore(notification.arenaId)}
                  className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-purple-300 bg-purple-900/50 rounded-md hover:bg-purple-900/80 transition-colors"
                >
                  <BookOpen size={12} /> Learn More
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  const renderOSChrome = () => {
    switch (osType.toLowerCase()) {
      case 'macos':
        return renderMacOSChrome();
      case 'windows':
      default:
        return renderWindowsChrome();
    }
  };
  
  const renderWindowsChrome = () => {
    return (
      <>
        {/* Windows Taskbar */}
        <div className="bg-blue-900 h-12 flex items-center justify-between px-2 border-t border-blue-700">
          {/* Start Button and Apps */}
          <div className="flex items-center space-x-1">
            <button 
              className={`p-2 rounded-md ${startMenuOpen ? 'bg-blue-700' : 'hover:bg-blue-800'}`}
              onClick={() => setStartMenuOpen(!startMenuOpen)}
            >
              <div className="h-6 w-6 bg-blue-500 flex items-center justify-center rounded-sm">
                <span className="text-xs font-bold">W</span>
              </div>
            </button>
            
            <button 
              className={`p-2 rounded-md ${activeApp === 'email' ? 'bg-blue-700' : 'hover:bg-blue-800'}`}
              onClick={() => launchApp('email')}
            >
              <Mail size={20} className="text-white" />
            </button>
            
            <button 
              className={`p-2 rounded-md ${activeApp === 'browser' ? 'bg-blue-700' : 'hover:bg-blue-800'}`}
              onClick={() => launchApp('browser')}
            >
              <Globe size={20} className="text-white" />
            </button>
            
            <button 
              className={`p-2 rounded-md ${activeApp === 'files' ? 'bg-blue-700' : 'hover:bg-blue-800'}`}
              onClick={() => launchApp('files')}
            >
              <Folder size={20} className="text-white" />
            </button>
            
            <button 
              className={`p-2 rounded-md ${activeApp === 'security' ? 'bg-blue-700' : 'hover:bg-blue-800'}`}
              onClick={() => launchApp('security')}
            >
              <Shield size={20} className="text-white" />
            </button>
          </div>
          
          {/* Right-side System Tray */}
          <div className="flex items-center space-x-3">
            {localNotifications.length > 0 && (
              <div 
                className="relative cursor-pointer" 
                onClick={handleNotificationClick}
              >
                <Bell size={16} className="text-white" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center text-xs">
                  {localNotifications.length}
                </span>
              </div>
            )}
            <div className="text-sm text-white">
              {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
        
        {/* Windows Start Menu */}
        {startMenuOpen && (
          <div className="absolute bottom-12 left-0 w-72 bg-gray-900 border border-gray-800 rounded-tr-lg shadow-xl z-50 overflow-hidden">
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center">
                <User size={32} className="text-blue-500 mr-3" />
                <div>
                  <div className="font-medium">User Name</div>
                  <div className="text-sm text-gray-400">user@company.com</div>
                </div>
              </div>
            </div>
            
            <div className="max-h-96 overflow-y-auto p-2">
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'email', name: 'Email', icon: <Mail size={24} /> },
                  { id: 'browser', name: 'Browser', icon: <Globe size={24} /> },
                  { id: 'files', name: 'Files', icon: <Folder size={24} /> },
                  { id: 'security', name: 'Security', icon: <Shield size={24} /> },
                  { id: 'calendar', name: 'Calendar', icon: <Calendar size={24} /> },
                  { id: 'settings', name: 'Settings', icon: <Settings size={24} /> },
                ].map(app => (
                  <button
                    key={app.id}
                    className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-gray-800"
                    onClick={() => launchApp(app.id)}
                  >
                    <div className="text-blue-500 mb-1">{app.icon}</div>
                    <span className="text-xs">{app.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* Render notification panel */}
        {renderNotificationPanel()}
      </>
    );
  };
  
  const renderMacOSChrome = () => {
    return (
      <>
        {/* macOS Menu Bar */}
        <div className="bg-gray-800 h-7 flex items-center justify-between px-4 text-white text-sm">
          <div className="flex items-center space-x-4">
            <span className="font-medium">Finder</span>
            <span>File</span>
            <span>Edit</span>
            <span>View</span>
            <span>Help</span>
          </div>
          
          <div className="flex items-center space-x-3">
            {localNotifications.length > 0 && (
              <div 
                className="relative cursor-pointer"
                onClick={handleNotificationClick}
              >
                <Bell size={14} className="text-white" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-3 w-3 flex items-center justify-center text-xs">
                  {localNotifications.length}
                </span>
              </div>
            )}
            <span>
              {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
        
        {/* macOS Dock */}
        <div className="absolute bottom-0 left-0 right-0 mx-auto w-auto flex justify-center">
          <div className="bg-gray-900/80 backdrop-blur-sm px-2 py-1 rounded-t-xl border border-gray-700 border-b-0 flex space-x-2">
            {[
              { id: 'finder', icon: <Folder size={32} className="text-blue-400" /> },
              { id: 'email', icon: <Mail size={32} className="text-purple-400" /> },
              { id: 'browser', icon: <Globe size={32} className="text-blue-500" /> },
              { id: 'security', icon: <Shield size={32} className="text-green-400" /> },
              { id: 'calendar', icon: <Calendar size={32} className="text-red-400" /> },
              { id: 'settings', icon: <Settings size={32} className="text-gray-400" /> },
            ].map(app => (
              <button
                key={app.id}
                className={`p-1 rounded-md hover:bg-white/10 transition-colors ${activeApp === app.id ? 'bg-white/10' : ''}`}
                onClick={() => launchApp(app.id)}
              >
                {app.icon}
              </button>
            ))}
          </div>
        </div>
        
        {/* Render notification panel */}
        {renderNotificationPanel()}
      </>
    );
  };
  
  // Render application window with title bar and content
  const renderAppWindow = (app) => {
    if (!app) return null;
    
    let title = '';
    let defaultContent = null;
    
    switch (app) {
      case 'email':
        title = 'Company Email';
        defaultContent = <div className="p-4 text-center text-gray-400">Email application content would be displayed here.</div>;
        break;
      case 'browser':
        title = 'Web Browser';
        defaultContent = <div className="p-4 text-center text-gray-400">Browser content would be displayed here.</div>;
        break;
      case 'files':
        title = 'File Explorer';
        defaultContent = <div className="p-4 text-center text-gray-400">File explorer content would be displayed here.</div>;
        break;
      case 'security':
        title = 'Security Center';
        defaultContent = <div className="p-4 text-center text-gray-400">Security center content would be displayed here.</div>;
        break;
      default:
        title = 'Application';
        defaultContent = <div className="p-4 text-center text-gray-400">Application content would be displayed here.</div>;
    }
    
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden shadow-lg w-full h-full flex flex-col">
        {/* Window title bar */}
        <div className="bg-gray-700 px-4 py-2 flex justify-between items-center border-b border-gray-600">
          <div className="font-medium">{title}</div>
          <div className="flex space-x-2">
            <button className="h-5 w-5 rounded-full bg-yellow-500 hover:bg-yellow-400 flex items-center justify-center">
              <Minimize2 size={12} className="text-yellow-800" />
            </button>
            <button className="h-5 w-5 rounded-full bg-green-500 hover:bg-green-400 flex items-center justify-center">
              <Maximize2 size={12} className="text-green-800" />
            </button>
            <button 
              className="h-5 w-5 rounded-full bg-red-500 hover:bg-red-400 flex items-center justify-center"
              onClick={() => setActiveApp(null)}
            >
              <X size={12} className="text-red-800" />
            </button>
          </div>
        </div>
        
        {/* Application content area */}
        <div className="flex-1 bg-gray-900 overflow-auto">
          {/* Render children for the active app, or default content if no children */}
          {children && app === activeApp ? children : defaultContent}
        </div>
      </div>
    );
  };
  
  return (
    <div className="relative w-full h-full bg-gray-950 flex flex-col">
      {/* Main OS Content Area */}
      <div className="flex-1 p-4 relative">
        {activeApp ? (
          renderAppWindow(activeApp)
        ) : (
          /* Desktop background and icons when no app is active */
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 p-2">
            {[
              { id: 'email', name: 'Email', icon: <Mail size={32} className="text-blue-400" /> },
              { id: 'browser', name: 'Internet', icon: <Globe size={32} className="text-blue-500" /> },
              { id: 'files', name: 'My Files', icon: <Folder size={32} className="text-yellow-500" /> },
              { id: 'security', name: 'Security', icon: <Shield size={32} className="text-green-500" /> },
              { id: 'document', name: 'Report.docx', icon: <FileText size={32} className="text-blue-400" /> },
            ].map(icon => (
              <button
                key={icon.id}
                className="flex flex-col items-center justify-center p-2 rounded-md hover:bg-white/10"
                onClick={() => launchApp(icon.id)}
              >
                <div className="mb-1">{icon.icon}</div>
                <span className="text-xs text-white text-center">{icon.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* OS Chrome (Taskbar, Dock, etc.) */}
      {renderOSChrome()}
    </div>
  );
};

export default SimulatedOS;